import { skipToken, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { MerchantData } from "types"
import { tintVTOService } from "services/tintvto"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { useAlert } from "providers/AlertProvider"

export const MERCHANT_QUERY_KEY = (merchantId: string) => ["merchant", merchantId]
const MERCHANTS_QUERY_KEY = ["merchants"]

export const useMerchantQuery = (merchantId?: string) => {
  return useQuery<MerchantData>({
    queryKey: MERCHANT_QUERY_KEY(merchantId),
    queryFn: merchantId
      ? async () => {
          const { data } = await tintVTOService.merchantById(merchantId)
          return data.merchantById
        }
      : skipToken,
  })
}

export const useSaveMerchantMutation = (merchantId?: string) => {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { showAlert } = useAlert()

  return useMutation({
    mutationFn: async (merchant: MerchantData) => {
      const response = await tintVTOService.saveMerchant(merchant)
      if (response.errors) throw new Error(response.errors[0].message)

      return response
    },
    onSuccess: (_, merchant) => {
      showAlert({
        error: false,
        text: t("merchantPage.saveSuccess", {
          name: merchant.name,
          action: merchant.id ? t("merchantPage.updated") : t("merchantPage.created"),
        }),
      })
      queryClient.resetQueries({ queryKey: MERCHANTS_QUERY_KEY })
      queryClient.resetQueries({ queryKey: MERCHANT_QUERY_KEY(merchantId) })
      navigate(`/merchants`)
    },
    onError: (error) => {
      showAlert({
        error: true,
        text: error.message || t("merchantPage.saveError"),
      })
    },
  })
}

export const useMerchantsQuery = (enabled: boolean = true) => {
  return useQuery({
    queryKey: MERCHANTS_QUERY_KEY,
    queryFn: async () => {
      const { data } = await tintVTOService.merchants()
      return data.merchants
    },
    enabled,
    retry: false, // Don't retry if it fails due to permissions
  })
}
